import React, { useState, useEffect } from 'react';
import Sidebar from './sidebar';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import Content from './content';
import Student from './student/student';
import sidebarData from './sidebardata.json';
import Course from './course/course';
import CourseDetail from './course/CourseDetail';
import Assignment from './assignment/assignment';
import Footer from './footer';
import Dashboard from './dashboard/today';
import SignOut from '../logout';
import Organisation from './admin/organisation';
import Packages from './admin/Packages/packages';
import QuestionsPage from './assignment/questionsGrid';
import CancelPage from './cancel';
import SuccessPage from './success';
import PackageDetails from './Package/package';
import AssessmentForm from './assignment/assessmentSettings';
import Setting from './System/setting'
import Score from './student/score'
import Result from './student/viewResult';
import AccountForm from './account';
import SettingsPage from './SettingsPage';
import GlobalStyle from './GlobalStyles';
import { ThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { buildTheme } from './theme';
import useTweaks from './ui/useTweaks';
import TweaksPanel from './ui/TweaksPanel';
import createInstructorMuiTheme from './ui/muiTheme';
import InstructorGlobalStyle from './ui/GlobalStyle';
import { Main, Viewport, Page } from './ui/primitives';
import { ThemeModeContext } from './ui/themeMode';
import { runThemeBubbleTransition } from './ui/themeModeTransition';
import TopBar from './ui/TopBar';
import ImpersonationFrame, { ImpersonationPendingOverlay } from './ui/ImpersonationFrame';
import { isImpersonating } from './impersonationView';
import { canUseImpersonationSearch } from './services/instructorPortalAccess';
import Training from './training/Training';
import TrainingQuestions from './training/questionsGrid'
import CourseSetting from './course/courseSettings';
import TrainingSetting from './training/trainingSetting'
import TrainingCompliance from './training/training&Compliance';
import CourseAssessmentSelector from './System/CourseTestSelector';
import ReactGA from 'react-ga4';
import ImportCourseGrid from './System/importCourseFeature'
import DemoAccessSettings from './System/DemoAccessSettings'
import CampaignManager from './System/CampaignManager'
import PublishingManager from './System/PublishingManager'
import ActivationCodeManager from './System/ActivationCodeManager'
import PromptsManager from './System/PromptsManager'
import QuestionSelectionManager from './System/QuestionSelectionManager'
import InstructorCourseCatalog from './course/InstructorCourseCatalog'
import Onboarding from './flows/Onboarding';
import FlowMap from './flows/FlowMap';
import CourseWizard from './course/CourseWizard';
import AnalysisPage from './analysis/AnalysisPage';
import AssessmentDetail from './assignment/AssessmentDetail';
import { useCourseList } from './hooks/useCourseList';
import { CourseFilterProvider } from './context/CourseFilterContext';
import { ConfirmProvider } from './ui/ConfirmProvider';
import { useAppContext } from '../appprovider';
import UpgradeGateDialog from './ui/UpgradeGateDialog';
import {
  RequireBillingAccess,
  RequireOrgAdminAccess,
  RequireSystemAccess,
} from './ui/PortalAccessGate';
import { DemoAccessGate } from './ui/DemoAccessGate';
import {
  AdminUsers,
  AdminCourseCatalog,
  AdminIntegrations,
  AdminLocationStandard,
  AdminPlanBilling,
  Billing,
  BillingInvoices,
  BillingIncomePage,
  SystemCourseCatalog,
  SystemApplicationUsers,
} from './admin/adminPages';

const ONBOARD_ROUTE = {
  dashboard: '/Portal/Instructor',
  courses: '/Portal/Instructor/Course',
  scores: '/Portal/Instructor/score',
  settings: '/Portal/Instructor/organisation',
  workflow: '/Portal/Instructor/Workflow',
};

function MyRouter({ isLoggedIn }) {
  const { loadEntitlements, gateOpen, gateBody, closeUpgradeGate, entitlements } = useAppContext();
  // Pre-warm the shared course cache as soon as the portal shell mounts.
  // All dialogs that call useCourseList() will resolve from memory instantly.
  useCourseList();

  const [selectedTitle, setSelectedTitle] = useState(null);
  const navigate = useNavigate();
  const userType = localStorage.getItem('userType');
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location]);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (isLoggedIn && storedUserId) {
      loadEntitlements(storedUserId);
    }
  }, [isLoggedIn, loadEntitlements]);

  const handleItemClick = (title) => {
    setSelectedTitle(title);
  };

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [tweaks, setTweak] = useTweaks();
  const mode = tweaks.theme === 'dark' ? 'dark' : 'light';
  const collapsed = tweaks.sidebar === 'icon';

  const setThemeWithBubble = React.useCallback((nextTheme, event) => {
    const target = nextTheme === 'dark' ? 'dark' : 'light';
    if (target === mode) return;
    runThemeBubbleTransition(event, () => setTweak('theme', target));
  }, [mode, setTweak]);

  const toggleModeFromEvent = React.useCallback((event) => {
    setThemeWithBubble(mode === 'dark' ? 'light' : 'dark', event);
  }, [mode, setThemeWithBubble]);

  const toggleMode = React.useCallback(() => {
    toggleModeFromEvent(null);
  }, [toggleModeFromEvent]);

  const toggleCollapsed = React.useCallback(() => {
    setTweak('sidebar', collapsed ? 'labeled' : 'icon');
  }, [collapsed, setTweak]);

  const scTheme = React.useMemo(() => buildTheme(tweaks), [tweaks]);
  const muiTheme = React.useMemo(() => createInstructorMuiTheme(scTheme), [scTheme]);

  // First-run onboarding (shown once, then replayable from the Workflow page).
  const [onboarded, setOnboarded] = useState(() => localStorage.getItem('onboardingComplete') === '1');
  const [obStep, setObStep] = useState('intro');
  const [wizardOpen, setWizardOpen] = useState(false);

  const enterApp = React.useCallback((route, opts) => {
    localStorage.setItem('onboardingComplete', '1');
    setOnboarded(true);
    navigate(ONBOARD_ROUTE[route] || ONBOARD_ROUTE.dashboard);
    if (opts && opts.wizard) setTimeout(() => setWizardOpen(true), 350);
  }, [navigate]);

  const replayOnboarding = React.useCallback((step) => {
    setObStep(step || 'intro');
    setOnboarded(false);
  }, []);

  useEffect(() => {
    window.openCourseWizard = () => setWizardOpen(true);
    window.__replayOnboarding = replayOnboarding;
    return () => { delete window.openCourseWizard; delete window.__replayOnboarding; };
  }, [replayOnboarding]);

  const disableNavbarRoutes = [
    "/Portal/Instructor/Training&ComplianceMSSP",
    "/Portal/Instructor/Training&ComplianceAdmin",
    "/Portal/Instructor/Training&ComplianceCompany"
  ];
  const shouldHideNavbar = disableNavbarRoutes.some(route => location.pathname.startsWith(route));
  const showImpersonationFrame = isImpersonating();
  const showImpersonationOverlay = canUseImpersonationSearch(userType, entitlements);
  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode, toggleModeFromEvent, setThemeWithBubble }}>
    <MuiThemeProvider theme={muiTheme}>
    <ThemeProvider theme={scTheme}>
    <ConfirmProvider>
      <GlobalStyle />
      <InstructorGlobalStyle />
      {isLoggedIn && !onboarded ? (
        <Onboarding key={obStep} initialStep={obStep} onEnterApp={enterApp} />
      ) : (
    <CourseFilterProvider>
    <div className="instructor-app-shell" style={{ '--sidebar-w': scTheme.sidebarW }}>
      {showImpersonationOverlay && <ImpersonationPendingOverlay />}
      {showImpersonationFrame && <ImpersonationFrame sidebarCollapsed={collapsed} />}
      <Sidebar sidebarData={sidebarData} handleItemClick={handleItemClick} isOpen={isSidebarOpen} toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} collapsed={collapsed} onToggleCollapse={toggleCollapsed}/>
      <Main as="main" className={isSidebarOpen ? 'sidebar-open' : ''}>
        {!shouldHideNavbar && <TopBar tweaks={tweaks} setTweak={setTweak} />}
        <Viewport>
          <Page>
            <Routes>
          <Route path="" element={<DemoAccessGate featureKey="nav.dashboard"><Content /></DemoAccessGate>} />
          <Route path="/addEnrollee" element={<DemoAccessGate featureKey="nav.enrollments"><Student selectedTitle={selectedTitle} /></DemoAccessGate>} />
          <Route path="/Course" element={<DemoAccessGate featureKey="nav.course"><Course selectedTitle={selectedTitle} /></DemoAccessGate>} />
          <Route path="/CourseDetail" element={<DemoAccessGate featureKey="nav.course"><CourseDetail /></DemoAccessGate>} />
          <Route path="/Assignment" element={<DemoAccessGate featureKey="nav.assessments"><Assignment selectedTitle={selectedTitle} /></DemoAccessGate>} />
          <Route path="/today" element={<DemoAccessGate featureKey="nav.dashboard"><Dashboard /></DemoAccessGate>} />
          <Route path="/profile" element={<DemoAccessGate featureKey="nav.settings"><SettingsPage /></DemoAccessGate>} />
          <Route path="/organisation" element={<Navigate to="/Portal/Instructor/settings?tab=org" replace />} />
          <Route path="/admin" element={<Navigate to="/Portal/Instructor/admin/users" replace />} />
          <Route path="/packages" element={<RequireSystemAccess><Packages/></RequireSystemAccess>} />
          <Route path="/Questions" element={<QuestionsPage />} />
          <Route path="/Success" element={<SuccessPage />} />
          <Route path="/Cancel" element={<CancelPage />} />
          <Route path="/packageDetails" element={<PackageDetails />} />
          <Route path="/AssessmentSetting" element={<AssessmentForm />} />
          <Route path="/setting" element={<RequireSystemAccess><DemoAccessGate featureKey="nav.system"><Setting/></DemoAccessGate></RequireSystemAccess>} />
          <Route path="/system/users" element={<RequireSystemAccess><SystemApplicationUsers/></RequireSystemAccess>} />
          <Route path="/score" element={<DemoAccessGate featureKey="nav.scores"><Score/></DemoAccessGate>} />
          <Route path="/resultDetails" element={<Result/>} />
          <Route path="/account" element={<DemoAccessGate featureKey="nav.settings"><SettingsPage /></DemoAccessGate>} />
          <Route path="/settings" element={<DemoAccessGate featureKey="nav.settings"><SettingsPage /></DemoAccessGate>} />
          <Route path="/CourseSetting" element={<DemoAccessGate featureKey="nav.course"><CourseSetting/></DemoAccessGate>} />
          {/* Admin / Billing — org admin only */}
          <Route path="/admin/users" element={<RequireOrgAdminAccess><DemoAccessGate featureKey="nav.admin"><AdminUsers/></DemoAccessGate></RequireOrgAdminAccess>} />
          <Route path="/admin/catalog" element={<RequireOrgAdminAccess><DemoAccessGate featureKey="nav.admin"><AdminCourseCatalog/></DemoAccessGate></RequireOrgAdminAccess>} />
          <Route path="/admin/integrations" element={<RequireOrgAdminAccess><DemoAccessGate featureKey="nav.admin"><AdminIntegrations/></DemoAccessGate></RequireOrgAdminAccess>} />
          <Route path="/admin/location" element={<RequireOrgAdminAccess><DemoAccessGate featureKey="nav.admin"><AdminLocationStandard/></DemoAccessGate></RequireOrgAdminAccess>} />
          <Route path="/admin/billing" element={<RequireOrgAdminAccess><DemoAccessGate featureKey="nav.admin"><AdminPlanBilling/></DemoAccessGate></RequireOrgAdminAccess>} />
          <Route path="/billing" element={<RequireBillingAccess><DemoAccessGate featureKey="nav.billing"><Billing/></DemoAccessGate></RequireBillingAccess>} />
          <Route path="/billing/invoices" element={<RequireBillingAccess><DemoAccessGate featureKey="nav.billing"><BillingInvoices/></DemoAccessGate></RequireBillingAccess>} />
          <Route path="/billing/income" element={<RequireBillingAccess><DemoAccessGate featureKey="nav.billing"><BillingIncomePage/></DemoAccessGate></RequireBillingAccess>} />
          <Route path="/system/catalog" element={<RequireSystemAccess><SystemCourseCatalog/></RequireSystemAccess>} />
          <Route path="/analysis" element={<AnalysisPage/>} />
          <Route path="/AssessmentDetail" element={<AssessmentDetail/>} />
          <Route path="/courseSelector" element={<DemoAccessGate featureKey="nav.registration"><CourseAssessmentSelector/></DemoAccessGate>}/>
          <Route path="/TrainingSetting" element={<DemoAccessGate featureKey="nav.training"><TrainingSetting/></DemoAccessGate>} />
          <Route path="/course-catalog" element={<DemoAccessGate featureKey="nav.register"><InstructorCourseCatalog/></DemoAccessGate>}/>
          <Route path="/ImportCourse" element={<RequireSystemAccess><ImportCourseGrid/></RequireSystemAccess>}/>
          <Route path="/system/demo-access" element={<RequireSystemAccess><DemoAccessSettings/></RequireSystemAccess>}/>
          <Route path="/system/campaigns" element={<RequireSystemAccess><CampaignManager/></RequireSystemAccess>}/>
          <Route path="/system/publishing" element={<RequireSystemAccess><PublishingManager/></RequireSystemAccess>}/>
          <Route path="/system/activation-codes" element={<RequireSystemAccess><ActivationCodeManager/></RequireSystemAccess>}/>
          <Route path="/system/prompts" element={<RequireSystemAccess><PromptsManager/></RequireSystemAccess>}/>
          <Route path="/system/question-selection" element={<RequireSystemAccess><QuestionSelectionManager/></RequireSystemAccess>}/>
          <Route path="/Register" element={<Navigate to="/Portal/Instructor/settings?tab=register" replace />}/>
          <Route path="/Workflow" element={<DemoAccessGate featureKey="nav.workflow"><FlowMap onReplay={replayOnboarding} onCreateCourse={() => setWizardOpen(true)} /></DemoAccessGate>} />
          {/* Training is available to all user types and all packages. */}
          <Route path="/TrainingSection" element={<DemoAccessGate featureKey="nav.training"><Training /></DemoAccessGate>} />
          <Route path="/TrainingQuestions" element={<DemoAccessGate featureKey="nav.training"><TrainingQuestions /></DemoAccessGate>} />
          {userType === '2' && (
              <>
                <Route path="/Training&ComplianceMSSP" element={<RequireSystemAccess><DemoAccessGate featureKey="nav.trainingCompliance"><TrainingCompliance selectedTitle={selectedTitle} /></DemoAccessGate></RequireSystemAccess>} />
                <Route path="/Training&ComplianceAdmin" element={<RequireSystemAccess><DemoAccessGate featureKey="nav.trainingCompliance"><TrainingCompliance selectedTitle={selectedTitle} /></DemoAccessGate></RequireSystemAccess>} />
                <Route path="/Training&ComplianceCompany" element={<RequireSystemAccess><DemoAccessGate featureKey="nav.trainingCompliance"><TrainingCompliance selectedTitle={selectedTitle} /></DemoAccessGate></RequireSystemAccess>} />
              </>
            )}
            </Routes>
          </Page>
        </Viewport>
        <Footer />
      </Main>
      {wizardOpen && (
        <CourseWizard
          onClose={() => setWizardOpen(false)}
          onComplete={() => { setWizardOpen(false); navigate('/Portal/Instructor/Course'); }}
        />
      )}
      <UpgradeGateDialog
        open={gateOpen}
        gateBody={gateBody}
        entitlements={entitlements}
        onClose={closeUpgradeGate}
      />
      {/* Single site-wide chatbot is the "Yaki AI Assistant" (ChatWidget),
          mounted globally in MyRouter.js. The older instructor ChatBotWidget
          was removed so only one assistant appears. */}
    </div>
    </CourseFilterProvider>
      )}
    </ConfirmProvider>
    </ThemeProvider>
    </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default MyRouter;
